// Crowd excitement (§13.5, §25): a smoothed 0..1 value computed from the FrameView and the KOs on
// the bus, written into `view.excitement` before render and audio run (§23.2). It drives the toy
// audience bounce (§12.6) and the crowd murmur. DOM- and three-free.
import { EV } from '@shared/events';
import { BS, RoomPhase, RoundPhase } from '@shared/ids';
import { onSim, type Bus } from '../core/bus';
import type { FrameView } from './FrameView';

/** §13.5 terms. */
export const EXCITEMENT = {
  /** While any bean teeters or hangs from a ledge. */
  edge: 0.35,
  /** Per KO; the sum decays linearly. */
  perKo: 0.2,
  koDecayPerSec: 0.3,
  suddenDeath: 0.15,
  /** Per bean below `fewAlive` still in the round. */
  perMissingBean: 0.1,
  fewAlive: 4,
  matchPoint: 0.3,
  /** Time constant of the smoothing toward the clamped sum, seconds. */
  smoothingSec: 0.25,
} as const;

function someoneOnTheEdge(view: FrameView): boolean {
  return view.beans.some((b) => b.active && !b.out && (b.state === BS.TEETER || b.state === BS.LEDGE));
}

/** The §13.5 sum without the KO term; the round terms apply while the round is played. */
function situation(view: FrameView): number {
  let sum = someoneOnTheEdge(view) ? EXCITEMENT.edge : 0;
  if (view.mode.matchPoint) sum += EXCITEMENT.matchPoint;
  if (view.roomPhase !== RoomPhase.PLAY) return sum;
  if (view.clock.phase === RoundPhase.SUDDEN_DEATH) sum += EXCITEMENT.suddenDeath;
  sum += EXCITEMENT.perMissingBean * Math.max(0, EXCITEMENT.fewAlive - view.mode.alive);
  return sum;
}

export class Excitement {
  private koSum = 0;
  private value = 0;
  private readonly unsubscribe: () => void;

  constructor(bus: Bus) {
    this.unsubscribe = onSim(bus, (ev) => {
      if (ev.type === EV.KO) this.koSum += EXCITEMENT.perKo;
    });
  }

  /** Advances by `view.dt` and writes `view.excitement`. */
  update(view: FrameView): void {
    const dt = view.dt;
    this.koSum = Math.max(0, this.koSum - EXCITEMENT.koDecayPerSec * dt);
    const target = Math.min(1, this.koSum + situation(view));
    this.value += (target - this.value) * (1 - Math.exp(-dt / EXCITEMENT.smoothingSec));
    view.excitement = this.value;
  }

  dispose(): void {
    this.unsubscribe();
  }
}
